/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import { Avatar, Button, Checkbox, Col, Divider, Form, Input, Radio, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { QuestionCircleOutlined, UserOutlined } from '@ant-design/icons';
import MultiDropdown from './Fields/MultiDropdown';
import BotAvatar from '../UI/BotAvatar';
import { LiveChatIcon } from '../Icons/LiveChatIcon';
import { GearIcon } from '../Icons/GearIcon';

// Misc imports
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG, PERMISSIONS_TENANT, DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import extractMsTeamsDataFromUrl from '../../utils/misc/extractMsTeamsDataFromUrl';
import formatEndUserName from '../../utils/string/formatEndUserName';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';
import { useCreateUserGroupMutation, useFetchUserGroupQuery, useUpdateUserGroupMutation } from '../../store/userGroups/userGroupsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const { useForm } = Form;
const { Option } = Select;
const GROUP_PLATFORMS = {
  MS_TEAMS: 'MS_TEAMS_CHANNEL',
  NONE: 'NONE'
};

const formatExternalChannelValues = (externalChannel) => {
  if (externalChannel && externalChannel.type === GROUP_PLATFORMS.NONE) {
    const formattedChannel = {
      ...externalChannel,
      type: null
    };

    return formattedChannel;
  } else if (externalChannel) {
    return externalChannel;
  }

  return {};
};

const UserGroupEditForm = ({
  setSelectedGroup,
  selectedGroup,
  onCancel,
  bodyClassName,
  footerClassName
}) => {
  const [form] = useForm();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();

  const { data: organization } = useFetchOrganizationQuery({ orgId });
  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId });
  const { data: usersThinList, isFetching: isFetchingUsersThinList, isLoading: isLoadingUsersThinList } = useFetchUsersThinListQuery({ orgId, urlParams: { pageSize: 500 } });
  const { data: userGroup, isLoading: isLoadingUserGroup, refetch: fetchUserGroup } = useFetchUserGroupQuery({ orgId, userGroupId: selectedGroup?.id }, { skip: !selectedGroup?.id });
  const [createUserGroup, { isLoading: isCreatingUserGroup }] = useCreateUserGroupMutation();
  const [updateUserGroup, { isLoading: isUpdatingUserGroup }] = useUpdateUserGroupMutation();
  const initialValues = useMemo(() => {
    return userGroup ? {
      name: userGroup.name,
      userIds: (userGroup.users || []).map(user => user.id),
      externalChannel: formatExternalChannelValues(userGroup.externalChannel),
      orgAccess: userGroup.organizationRoles.find(role => Object.values(PERMISSIONS_ORG).includes(role)) || '',
      appRoles: userGroup.organizationRoles.filter(role => Object.values(ADDITIONAL_ROLES_ORG).includes(role)),
      botIds: Object.entries(userGroup.tenantRoles).map(([botId, role]) => botId),
      createdAt: userGroup.createdAt,
      updatedAt: userGroup.updatedAt
    } : {
      name: '',
      userIds: [],
      externalChannel: {},
      orgAccess: '',
      appRoles: [],
      botIds: []
    };
  }, [userGroup]);

  const [tenantRoles, setTenantRoles] = useState({});

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  useEffect(() => {
    if (userGroup) {
      form.resetFields();
      setTenantRoles(userGroup.tenantRoles || {});
    }
  }, [userGroup]);

  const onSubmit = async () => {
    const { name, orgAccess, appRoles, externalChannel, userIds } = form.getFieldsValue(true);
    externalChannel.id = externalChannel.aadGroupId;
    let valuesToSubmit = {};

    if (userGroup) {
      const organizationRoles = [...appRoles, ...userGroup.organizationRoles.filter(role => !Object.values(PERMISSIONS_ORG).includes(role) && !Object.values(ADDITIONAL_ROLES_ORG).includes(role))];

      if (orgAccess) {
        organizationRoles.push(orgAccess);
      }

      valuesToSubmit = {
        userIds,
        organizationRoles,
        tenantRoles: tenantRoles,
        externalChannel: externalChannel.type ? externalChannel : { type: GROUP_PLATFORMS.NONE }
      };

      if (userGroup.name !== name) {
        valuesToSubmit.name = name;
      }
    } else {
      const organizationRoles = [...appRoles];

      if (orgAccess) {
        organizationRoles.push(orgAccess);
      }

      valuesToSubmit = {
        name,
        userIds,
        organizationRoles,
        organizationId: orgId,
        tenantRoles: tenantRoles,
        externalChannel: externalChannel.type ? externalChannel : { type: GROUP_PLATFORMS.NONE }
      };
    }

    let isSaveSuccessful;

    if (userGroup) {
      isSaveSuccessful = await updateUserGroup({ orgId, userGroupId: userGroup.id, data: valuesToSubmit }).unwrap();
    } else {
      isSaveSuccessful = await createUserGroup({ orgId, data: valuesToSubmit }).unwrap();
    }

    if (isSaveSuccessful) {
      if (selectedGroup.id) {
        fetchUserGroup();
      } else {
        setSelectedGroup(null);
      }
    }
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        {
          isLoadingUserGroup || isLoadingUsersThinList ? (
            <Row justify="center">
              <Col className="p-5">
                <Spin />
              </Col>
            </Row>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              initialValues={initialValues}
            >
              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('User Group Details')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Configure the name of the user group')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    name="name"
                    label={t('Group Name')}
                    required
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[{
                      required: true,
                      type: 'string',
                      whitespace: true,
                      message: t('Please enter a name for this group')
                    }]}
                  >
                    <Input size="large" placeholder={t('Enter a name for your group')}/>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Group Members')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Add Chatbricks users to this group')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    label={t('Members')}
                    name={['userIds']}
                  >
                    <MultiDropdown
                      size="large"
                      placeholder={t('Select members')}
                      allowClear
                      loading={isFetchingUsersThinList}
                      autoClearSearchValue={false}
                      hideExtraFromDropdown
                      options={(usersThinList || [])
                        .map(user => ({
                          label: (
                            <Space size={4}>
                              <Avatar size={22}>
                                {user.fullName ? formatEndUserName(user.fullName) : <UserOutlined />}
                              </Avatar>
                              {user.fullName}
                            </Space>
                          ),
                          value: user.id,
                          searchValue: user.fullName,
                          extra: <Space className="text-muted fs-sm ml-1">- {user.email}</Space>
                        }))
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Associate with External Channel')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Connect to an external organization')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    name={['externalChannel', 'type']}
                    label={t('Channel Type')}
                  >
                    <Select
                      placeholder={t('Select a platform')}
                      allowClear
                      size="large"
                      className="fs-base"
                    >
                      <Option value={GROUP_PLATFORMS.MS_TEAMS}>{t('Microsoft Teams')}</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate
                  >
                    {() => {
                      if (form.getFieldValue(['externalChannel', 'type'])) {
                        return (
                          <Fragment>
                            <Typography.Paragraph className="mb-2">{t('Channel URL')}</Typography.Paragraph>

                            <Form.Item
                              name="channelURL"
                              rules={[
                                {
                                  validator: async (_rule, value) => {
                                    if (form.getFieldValue('channelURL')) {
                                      return extractMsTeamsDataFromUrl(value).then(result => {
                                        form.setFieldsValue({
                                          externalChannel: {
                                            channelId: result.channelId || form.getFieldValue(['externalChannel', 'channelId']),
                                            azureTenantId: result.azureTenantId || form.getFieldValue(['externalChannel', 'azureTenantId']),
                                            aadGroupId: result.aadGroupId || form.getFieldValue(['externalChannel', 'aadGroupId'])
                                          }
                                        });

                                        return Promise.resolve();
                                      }).catch((err) => {
                                        form.setFieldsValue({
                                          externalChannel: {
                                            channelId: '',
                                            azureTenantId: '',
                                            aadGroupId: ''
                                          }
                                        });
                                        return Promise.resolve();
                                      });
                                    }
                                  }
                                }
                              ]}
                            >
                              <Input size="large" placeholder={t('Paste the channel URL here')}/>
                            </Form.Item>

                            <div className="w-100 py-2 px-4 bg-body border-radius-base">
                              <Typography.Text className="fs-sm fw-500">
                                {t('Channel ID')}
                              </Typography.Text>

                              <Form.Item
                                name={['externalChannel', 'channelId']}
                                labelAlign="left"
                                className="mb-1"
                              >
                                <Input
                                  className="fs-sm fw-bold bg-none p-0"
                                  bordered={false}
                                  placeholder={t('Enter a channel URL')}
                                  readOnly
                                />
                              </Form.Item>

                              <Typography.Text className="fs-sm fw-500">
                                {t('Azure Tenant ID')}
                              </Typography.Text>

                              <Form.Item
                                name={['externalChannel', 'azureTenantId']}
                                labelAlign="left"
                                className="mb-1"
                              >
                                <Input
                                  className="fs-sm fw-bold bg-none p-0"
                                  bordered={false}
                                  placeholder={t('Enter a channel URL')}
                                  readOnly
                                />
                              </Form.Item>

                              <Typography.Text className="fs-sm fw-500">
                                {t('Team AD ID')}
                              </Typography.Text>

                              <Form.Item
                                name={['externalChannel', 'aadGroupId']}
                                labelAlign="left"
                                className="mb-1"
                              >
                                <Input
                                  className="fs-sm fw-bold bg-none p-0"
                                  bordered={false}
                                  placeholder={t('Enter a channel URL')}
                                  readOnly
                                />
                              </Form.Item>
                            </div>
                          </Fragment>
                        );
                      }

                      return null;
                    }}
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Workspace Level Access')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Set Workspace access and permissions for the group')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Row align="top">
                    <Col span={12}>
                      <Form.Item
                        label={t('Access Roles')}
                        tooltip={{
                          className: 'ml-2',
                          icon: <QuestionCircleOutlined />,
                          title: t('Select the group\'s access level')
                        }}
                        name="orgAccess"
                        labelCol={{ span: 24 }}
                      >
                        <Radio.Group>
                          <Space direction="vertical">
                            <Radio value={''}>{t('No Workspace access')}</Radio>
                            <Radio value={PERMISSIONS_ORG.VIEWER}>{t('Workspace viewer')}</Radio>
                            <Radio value={PERMISSIONS_ORG.EDITOR}>{t('Workspace editor')}</Radio>
                            <Radio value={PERMISSIONS_ORG.ADMIN}>{t('Workspace admin')}</Radio>
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="appRoles"
                        tooltip={{
                          className: 'ml-2',
                          icon: <QuestionCircleOutlined />,
                          title: t('Add roles that grant permission to certain applications (e.g. LiveChat, HRwiz)')
                        }}
                        label={<Typography.Text>{t('Application Roles')}</Typography.Text>}
                        labelCol={{ span: 24 }}
                      >
                        <Checkbox.Group>
                          <Space direction="vertical">
                            <Checkbox value={ADDITIONAL_ROLES_ORG.LIVE_AGENT}>
                              <Tag className="fs-base border-radius-base px-2 py-1">
                                <LiveChatIcon className="fs-lg" />
                              </Tag>
                              {t('Live Agent')}
                            </Checkbox>

                            <Checkbox value={ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN}>
                              <Tag className="fs-base border-radius-base px-2 py-1">
                                <LiveChatIcon className="fs-lg"/>
                                <GearIcon className="fs-sm v-align-baseline" />
                              </Tag>
                              {t('LiveChat Admin')}
                            </Checkbox>
                          </Space>
                        </Checkbox.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Agent Level Access')}
                  </Typography.Paragraph>
                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Set agent access and permissions for the group')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    label={t('Access to Agents')}
                    name="botIds"
                  >
                    <MultiDropdown
                      size="large"
                      placeholder={t('Select Agents')}
                      allowClear
                      loading={isFetchingBots}
                      autoClearSearchValue={false}
                      hideExtraFromDropdown
                      onChange={(values) => {
                        let newTenantRoles = {};

                        // If a new bot has been added or edited, set it's role to the new one (or the default)
                        values.forEach((value) => {
                          newTenantRoles[value] = tenantRoles[value] || [PERMISSIONS_TENANT.VIEWER];
                        });

                        // If a bot has been removed, set it's role to null
                        Object.keys(tenantRoles).forEach((key) => {
                          if (!values.includes(key)) {
                            newTenantRoles[key] = null;
                          }
                        });

                        setTenantRoles(newTenantRoles);
                      }}
                      options={(bots?.items || [])
                        .map(bot => ({
                          label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                          value: bot.id,
                          searchValue: bot.displayName,
                          extra: (
                            <Select
                              value={Object.entries(tenantRoles).find(([botId, role]) => botId === bot.id)?.[1] || PERMISSIONS_TENANT.VIEWER}
                              className="ml-auto"
                              style={{ width: 100 }}
                              options={[
                                {
                                  label: t('Viewer'),
                                  value: PERMISSIONS_TENANT.VIEWER
                                },
                                {
                                  label: t('Editor'),
                                  value: PERMISSIONS_TENANT.EDITOR
                                },
                                {
                                  label: t('Admin'),
                                  value: PERMISSIONS_TENANT.ADMIN
                                }
                              ]}
                              onSelect={(value) => {
                                setTenantRoles({ ...tenantRoles, [bot.id]: [value] });
                              }}
                            />
                          )
                        }))
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )
        }
      </div>

      <div className={footerClassName}>
        <Row justify="space-between">
          <Col>
            {
              (form.getFieldValue('createdAt') && form.getFieldValue('modifiedAt')) ? (
                <Col>
                  <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                    {t('Created')}: {form.getFieldValue('createdBy')}, {moment.tz(form.getFieldValue('createdAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                  </Typography.Paragraph>

                  <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                    {t('Last modified')}:  {form.getFieldValue('modifiedBy') ?? form.getFieldValue('createdBy')}, {moment.tz(form.getFieldValue('modifiedAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                  </Typography.Paragraph>
                </Col>
              ) : null
            }
          </Col>

          <Col>
            <Space>
              <Button
                size="large"
                onClick={onCancel}
                disabled={isCreatingUserGroup || isUpdatingUserGroup}
              >
                {t('Cancel')}
              </Button>

              <Button
                type="primary"
                size="large"
                className="d-block ml-auto"
                onClick={() => form.submit()}
                loading={isCreatingUserGroup || isUpdatingUserGroup}
              >
                {selectedGroup?.id || form.getFieldValue('createdAt') ? t('Save Changes') : t('Create Group')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

UserGroupEditForm.propTypes = {
  setSelectedGroup: PropTypes.func,
  selectedGroup: PropTypes.string,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default UserGroupEditForm;
